/**
 * Derive a full design token palette from a single primary hex color.
 * All other colors (hover, background, error, layout, etc.) are computed
 * algorithmically so the admin only needs one color picker.
 */

type RGB = [number, number, number];
type HSL = [number, number, number];

function hexToRgb(hex: string): RGB {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex([r, g, b]: RGB): string {
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

function rgbToHsl([r, g, b]: RGB): HSL {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  switch (max) {
    case rn:
      h = (gn - bn) / d + (gn < bn ? 6 : 0);
      break;
    case gn:
      h = (bn - rn) / d + 2;
      break;
    default:
      h = (rn - gn) / d + 4;
  }
  h /= 6;
  return [h, s, l];
}

function hslToRgb([h, s, l]: HSL): RGB {
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }
  const hue2rgb = (p: number, q: number, t: number) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    Math.round(hue2rgb(p, q, h) * 255),
    Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
  ];
}

function adjustLightness(hex: string, delta: number): string {
  const hsl = rgbToHsl(hexToRgb(hex));
  hsl[2] = Math.max(0, Math.min(1, hsl[2] + delta));
  return rgbToHex(hslToRgb(hsl));
}

export function hexToHslTriplet(hex: string): string {
  const [h, s, l] = rgbToHsl(hexToRgb(hex));
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export interface DerivedPalette {
  primary: string;
  primaryHover: string;
  primaryBg: string;
  error: string;
  errorHover: string;
  background: string;
  sidebarBackground: string;
  cardBackground: string;
}

export interface AvatarGradientPair {
  from: string;
  to: string;
}

export function derivePalette(
  primaryHex: string,
  isDark: boolean,
): DerivedPalette {
  return {
    primary: primaryHex,
    primaryHover: adjustLightness(primaryHex, isDark ? 0.1 : 0.12),
    primaryBg: isDark
      ? adjustLightness(primaryHex, -0.35)
      : adjustLightness(primaryHex, 0.38),
    error: '#ff4d4f',
    errorHover: '#ff7875',
    background: isDark ? '#141414' : '#f7f7f7',
    sidebarBackground: isDark ? '#1a1a1a' : '#ffffff',
    cardBackground: isDark ? '#1f1f1f' : '#ffffff',
  };
}

export function deriveAvatarGradientPairs(
  primaryHex: string,
  secondaryHex?: string,
): AvatarGradientPair[] {
  if (secondaryHex) {
    return [
      { from: adjustLightness(primaryHex, 0.12), to: primaryHex },
      { from: adjustLightness(secondaryHex, 0.14), to: secondaryHex },
      { from: adjustLightness(primaryHex, -0.08), to: secondaryHex },
      {
        from: adjustLightness(secondaryHex, -0.1),
        to: adjustLightness(primaryHex, 0.06),
      },
    ];
  }

  return [
    { from: adjustLightness(primaryHex, 0.2), to: primaryHex },
    { from: primaryHex, to: adjustLightness(primaryHex, -0.08) },
    {
      from: adjustLightness(primaryHex, 0.12),
      to: adjustLightness(primaryHex, -0.14),
    },
    {
      from: adjustLightness(primaryHex, 0.04),
      to: adjustLightness(primaryHex, -0.22),
    },
  ];
}

/**
 * Generate a 5-color marble palette from primary (and optional secondary) hex.
 * When secondaryHex is provided, it replaces two of the five slots so the
 * marble pattern becomes a blend of both brand colors.
 */
export function deriveMarblePalette(
  primaryHex: string,
  secondaryHex?: string,
): string[] {
  const hsl = rgbToHsl(hexToRgb(primaryHex));
  const [h, s] = hsl;

  const offsets = [
    { dh: 0.0, ds: 0.0, dl: 0.15 },
    { dh: -0.06, ds: -0.05, dl: 0.0 },
    { dh: 0.08, ds: 0.05, dl: -0.1 },
    { dh: 0.14, ds: -0.15, dl: 0.25 },
    { dh: -0.1, ds: 0.1, dl: -0.15 },
  ];

  const palette = offsets.map(({ dh, ds, dl }) => {
    const nh = (((h + dh) % 1) + 1) % 1;
    const ns = Math.max(0.15, Math.min(1, s + ds));
    const nl = Math.max(0.25, Math.min(0.85, hsl[2] + dl));
    return rgbToHex(hslToRgb([nh, ns, nl]));
  });

  if (secondaryHex) {
    const sHsl = rgbToHsl(hexToRgb(secondaryHex));
    palette[1] = secondaryHex;
    const lighter: HSL = [
      sHsl[0],
      Math.min(1, sHsl[1] + 0.05),
      Math.min(0.85, sHsl[2] + 0.2),
    ];
    palette[3] = rgbToHex(hslToRgb(lighter));
  }

  return palette;
}

export const DEFAULT_PRIMARY_COLOR = '#1677ff';
